import type {ReactElement} from 'react';

import dayjs from 'dayjs';
import {makeAutoObservable, reaction} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {
  CoreDevClearStorageParams,
  CoreDevGetTimeParams,
  CoreDevResetTimeParams,
  CoreDevSetTimeParams,
} from '../../api/types/core/methods';
import type {DateTimeObj} from '../../utils/datetime';
import type {
  Destructible,
  WithBusyManager,
  WithReactionDispose,
} from '../../utils/store';

import {apiRequest} from '../../api/utils/apiRequest';
import {alert} from '../../components/alert';
import {COMMAND} from '../../services/entity-action/utils';
import {
  datetimeObjToISOString,
  getSecondsFromMidnight,
  ISOStringToDatetimeObj,
} from '../../utils/datetime';
import {envManager} from '../../utils/env';
import {logger} from '../../utils/logger';
import {platform} from '../../utils/platform';
import {reloadApp} from '../../utils/reloadApp';
import {storage} from '../../utils/storage';
import {AsyncBusyManager, ReactionDisposeManager} from '../../utils/store';

type NowResponse = {
  now: DateTimeObj;
};

type ReasonKey = 'resetDb' | 'resetTime' | 'setTime';

type RenderSetTimeFn = (
  date: Date,
  onDateChange: (date: Date) => void,
  onTimeChange: (time: Date) => void,
) => ReactElement<any>;

export class GodModeStore
  implements Destructible, WithBusyManager<ReasonKey>, WithReactionDispose
{
  readonly busy = new AsyncBusyManager<ReasonKey>();
  disposer = new ReactionDisposeManager();
  isActive = false;
  private refreshTimeTimeout = 0;
  private renderSetTime: RenderSetTimeFn;
  private requestTimeTimeout = 0;
  private time = '';

  constructor(renderSetTime: RenderSetTimeFn) {
    this.renderSetTime = renderSetTime;

    this.busy.register(['setTime', 'resetTime', 'resetDb']);

    makeAutoObservable(this, undefined, {autoBind: true});

    this.disposer.watch(
      'time',
      reaction(
        () => this.time,
        time => {
          clearTimeout(this.requestTimeTimeout);

          if (time) {
            this.refreshTimeTimeout = +setTimeout(() => {
              const newTime = dayjs(time).add(1, 'second').toISOString();

              this.setTime(newTime);
            }, 1000);
          }
        },
      ),
    );

    this.disposer.watch(
      'env',
      reaction(
        () => envManager.isReady,
        isReady => {
          if (!isReady) {
            return;
          }

          this.isActive = [
            platform.isNative ? '' : 'dev',
            'stage1',
            'stage2',
          ].includes(envManager.get('appEnv'));

          if (this.isActive) {
            this.getServerTime();
          }
        },
        {
          fireImmediately: true,
        },
      ),
    );
  }

  destruct() {
    clearTimeout(this.requestTimeTimeout);
    clearTimeout(this.refreshTimeTimeout);

    this.disposer.dispose();
  }

  get timeString() {
    return this.time ? dayjs(this.time).format('DD-MM-YYYY HH:mm') : '...';
  }

  showActions() {
    alert.show({
      buttons: [
        {
          label: i18n.t('shared:set_time'),
          onClick: () => {
            let newTime = dayjs(this.time);

            alert.show({
              buttons: [
                {
                  label: i18n.t('shared:close'),
                },
                {
                  label: i18n.t('shared:set_time'),
                  onClick: () => {
                    this.setServerTime(newTime.toDate());
                  },
                  scheme: 'secondary',
                },
              ],
              renderMessage: () =>
                this.renderSetTime(
                  newTime.toDate(),
                  date => {
                    newTime = dayjs(date)
                      .startOf('day')
                      .add(getSecondsFromMidnight(newTime.toDate()), 'seconds');
                  },
                  time => {
                    newTime = newTime
                      .startOf('day')
                      .add(getSecondsFromMidnight(time), 'seconds');
                  },
                ),

              title: i18n.t('shared:set_time'),
            });
          },
          scheme: 'secondary',
        },
        {
          label: i18n.t('shared:reset_time'),
          onClick: () => {
            this.resetTime();
          },
          scheme: 'secondary',
        },
        {
          label: i18n.t('shared:reload'),
          onClick: () => {
            this.reload();
          },
          scheme: 'secondary',
        },
        {
          label: i18n.t('shared:reset_db'),
          onClick: () => {
            this.resetDb();
          },
          scheme: 'error',
        },
        {
          label: i18n.t('shared:close'),
        },
      ],
      title: i18n.t('shared:god_mode'),
      variant: 'promo',
    });
  }

  private async getServerTime() {
    if (!this.isActive) {
      return;
    }

    try {
      const {now}: NowResponse = await apiRequest<CoreDevGetTimeParams>({
        method: 'dev.get_time',
        options: {
          withAuth: false,
        },
      });

      const serverTime = dayjs(datetimeObjToISOString(now)).toISOString();

      this.setTime(serverTime);
    } catch (error) {
      logger.error(error);

      this.requestTimeTimeout = +setTimeout(() => {
        this.getServerTime();
      }, 5000);
    }
  }

  private async reload() {
    try {
      if (!(await alert.confirm())) {
        return;
      }

      reloadApp();
    } catch (error) {
      logger.error(error);
      alert.error(error);
    }
  }

  private async resetDb() {
    try {
      if (!(await alert.confirm())) {
        return COMMAND.CANCEL;
      }

      this.busy.setReason('resetDb', true);

      await apiRequest<CoreDevClearStorageParams>({
        method: 'dev.clear_storage',
        options: {
          withAuth: false,
        },
      });

      await storage.clear();

      reloadApp();
    } catch (error) {
      logger.error(error);
      alert.error(error);
    } finally {
      this.busy.setReason('resetDb', false);
    }
  }

  private async resetTime() {
    try {
      if (!(await alert.confirm())) {
        return COMMAND.CANCEL;
      }

      this.busy.setReason('resetTime', true);

      const {now}: NowResponse = await apiRequest<CoreDevResetTimeParams>({
        method: 'dev.reset_time',
        options: {
          withAuth: false,
        },
      });

      const serverTime = dayjs(datetimeObjToISOString(now)).toISOString();

      this.setTime(serverTime);
    } catch (error) {
      logger.error(error);
      alert.error(error);
    } finally {
      this.busy.setReason('resetTime', false);
    }
  }

  private async setServerTime(date: Date) {
    try {
      if (!(await alert.confirm())) {
        return COMMAND.CANCEL;
      }

      this.busy.setReason('setTime', true);

      const {now}: NowResponse = await apiRequest<CoreDevSetTimeParams>({
        method: 'dev.set_time',
        options: {
          withAuth: false,
        },
        params: {
          time: ISOStringToDatetimeObj(date.toISOString()),
        },
      });

      const serverTime = dayjs(datetimeObjToISOString(now)).toISOString();

      this.setTime(serverTime);
    } catch (error) {
      logger.error(error);
      alert.error(error);
    } finally {
      this.busy.setReason('setTime', false);
    }
  }

  private setTime(time: string) {
    clearTimeout(this.refreshTimeTimeout);

    this.time = time;
  }
}
